import {
  CA_DATE_DAY_MONTH_FORMAT,
  CA_DATE_MID_FORMAT,
  CA_DATE_SHORT_DAY_MONTH_FORMAT,
  CA_DATE_SHORT_DELIMITED_FORMAT,
  CA_DATE_SHORT_FORMAT,
  CA_DATE_SHORT_READABLE_FORMAT,
  CA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  CA_DATE_TIMESTAMP_FORMAT,
  CA_DATE_TIMESTAMP_SHORT_FORMAT,
  CA_DATE_TODAY_FORMAT,
  DATE_DAY_MONTH_FORMAT,
  DATE_MID_FORMAT,
  DATE_SHORT_DAY_MONTH_FORMAT,
  DATE_SHORT_DELIMITED_FORMAT,
  DATE_SHORT_FORMAT,
  DATE_SHORT_READABLE_FORMAT,
  DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  DATE_TIMESTAMP_FORMAT,
  DATE_TIMESTAMP_SHORT_FORMAT,
  DATE_TODAY_FORMAT,
  US_DATE_DAY_MONTH_FORMAT,
  US_DATE_MID_FORMAT,
  US_DATE_SHORT_DAY_MONTH_FORMAT,
  US_DATE_SHORT_DELIMITED_FORMAT,
  US_DATE_SHORT_FORMAT,
  US_DATE_SHORT_READABLE_FORMAT,
  US_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  US_DATE_TIMESTAMP_FORMAT,
  US_DATE_TIMESTAMP_SHORT_FORMAT,
  US_DATE_TODAY_FORMAT,
  ZA_DATE_DAY_MONTH_FORMAT,
  ZA_DATE_MID_FORMAT,
  ZA_DATE_SHORT_DAY_MONTH_FORMAT,
  ZA_DATE_SHORT_DELIMITED_FORMAT,
  ZA_DATE_SHORT_FORMAT,
  ZA_DATE_SHORT_READABLE_FORMAT,
  ZA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  ZA_DATE_TIMESTAMP_FORMAT,
  ZA_DATE_TIMESTAMP_SHORT_FORMAT,
  ZA_DATE_TODAY_FORMAT,
} from '@payaca/constants/formatConstants';
import {
  MOMENT_CA_DATE_MID_FORMAT,
  MOMENT_CA_DATE_SHORT_DELIMITED_FORMAT,
  MOMENT_CA_DATE_SHORT_FORMAT,
  MOMENT_CA_DATE_SHORT_READABLE_FORMAT,
  MOMENT_CA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  MOMENT_CA_DATE_TIMESTAMP_FORMAT,
  MOMENT_CA_DATE_TIMESTAMP_SHORT_FORMAT,
  MOMENT_CA_DATE_TODAY_FORMAT,
  MOMENT_DATE_MID_FORMAT,
  MOMENT_DATE_SHORT_DELIMITED_FORMAT,
  MOMENT_DATE_SHORT_FORMAT,
  MOMENT_DATE_SHORT_READABLE_FORMAT,
  MOMENT_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  MOMENT_DATE_TIMESTAMP_FORMAT,
  MOMENT_DATE_TIMESTAMP_SHORT_FORMAT,
  MOMENT_DATE_TODAY_FORMAT,
  MOMENT_US_DATE_MID_FORMAT,
  MOMENT_US_DATE_SHORT_DELIMITED_FORMAT,
  MOMENT_US_DATE_SHORT_FORMAT,
  MOMENT_US_DATE_SHORT_READABLE_FORMAT,
  MOMENT_US_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  MOMENT_US_DATE_TIMESTAMP_FORMAT,
  MOMENT_US_DATE_TIMESTAMP_SHORT_FORMAT,
  MOMENT_US_DATE_TODAY_FORMAT,
  MOMENT_ZA_DATE_MID_FORMAT,
  MOMENT_ZA_DATE_SHORT_DELIMITED_FORMAT,
  MOMENT_ZA_DATE_SHORT_FORMAT,
  MOMENT_ZA_DATE_SHORT_READABLE_FORMAT,
  MOMENT_ZA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  MOMENT_ZA_DATE_TIMESTAMP_FORMAT,
  MOMENT_ZA_DATE_TIMESTAMP_SHORT_FORMAT,
  MOMENT_ZA_DATE_TODAY_FORMAT,
} from '@payaca/constants/momentFormatConstants';
import { AccountRegions } from '@payaca/types/accountTypes';
import { CurrencyCodes } from '@payaca/types/currencyTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { checkUnreachable } from '@payaca/utilities/guards';
import { currencyCodeForRegion } from './currencyHelper';

const regionalTextStringMappings: Partial<
  Record<AccountRegions, Partial<Record<RegionalStrings, string>>>
> = {
  [AccountRegions.AUSTRALIA]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '$',
    [RegionalStrings.VALUE_ADDED_TAX]: 'GST',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]:
      'Australian Business Number (ABN)',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[GST] - must be empty (no GST) or '10'`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[GST] - must be empty (no GST) or '10'`,
    [RegionalStrings.SORT_CODE]: 'BSB',
    [RegionalStrings.POSTCODE]: 'Postcode',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'State',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customisation',
    [RegionalStrings.INVOICE_TITLE]: 'Tax Invoice',
  },
  [AccountRegions.CANADA]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '$',
    [RegionalStrings.VALUE_ADDED_TAX]: 'GST/HST',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]: 'GST/HST number',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[GST/HST] - must be empty (no GST), '0', '5', '13', or '15`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[GST/HST] - must be empty (no GST), '0', '5', '13', or '15`,
    [RegionalStrings.SORT_CODE]: 'Routing number',
    [RegionalStrings.POSTCODE]: 'ZIP code',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'Province',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customization',
    [RegionalStrings.INVOICE_TITLE]: 'Invoice',
  },
  [AccountRegions.IRELAND]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '€',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[VAT] - must be empty (no VAT), '0', '9', '13.5', or '23'`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[VAT] - must be empty (no VAT), '0', '9', '13.5', or '23'`,
  },
  [AccountRegions.NEW_ZEALAND]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '$',
    [RegionalStrings.VALUE_ADDED_TAX]: 'GST',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]: 'GST number',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[GST] - must be empty (no GST) or '15'`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[GST] - must be empty (no GST) or '15'`,
    [RegionalStrings.SORT_CODE]: 'BSB',
    [RegionalStrings.POSTCODE]: 'Postcode',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'Region',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customisation',
    [RegionalStrings.INVOICE_TITLE]: 'Invoice',
  },
  [AccountRegions.SOUTH_AFRICA]: {
    [RegionalStrings.CURRENCY_SYMBOL]: 'R',
    [RegionalStrings.VALUE_ADDED_TAX]: 'VAT',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]: 'VAT number',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[VAT] - must be empty (no VAT), '0', or '15'`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[VAT] - must be empty (no VAT), '0', or '15'`,
    [RegionalStrings.SORT_CODE]: 'Branch code',
    [RegionalStrings.POSTCODE]: 'Postcode',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'Province',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customisation',
    [RegionalStrings.INVOICE_TITLE]: 'Invoice',
  },
  [AccountRegions.UK]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '£',
    [RegionalStrings.VALUE_ADDED_TAX]: 'VAT',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]: 'VAT number',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[VAT] - must be empty (no VAT), '0', '5' or '20' or '20 RC' (reverse charge)`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[VAT] - must be empty (no VAT), '0', '5' or '20'`,
    [RegionalStrings.SORT_CODE]: 'Sort code',
    [RegionalStrings.POSTCODE]: 'Postcode',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'County',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customisation',
    [RegionalStrings.INVOICE_TITLE]: 'Invoice',
  },
  [AccountRegions.US]: {
    [RegionalStrings.CURRENCY_SYMBOL]: '$',
    [RegionalStrings.VALUE_ADDED_TAX]: 'Sales tax',
    [RegionalStrings.VALUE_ADDED_TAX_NUMBER]: 'Sales tax number',
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER]: `[Sales Tax] - must be empty (no Sales tax)`,
    [RegionalStrings.VALUE_ADDED_TAX_CSV_HELPER_NO_RC]: `[Sales Tax] - must be empty (no Sales tax)`,
    [RegionalStrings.SORT_CODE]: 'Routing number',
    [RegionalStrings.POSTCODE]: 'ZIP code',
    [RegionalStrings.ADMINISTRATIVE_AREA]: 'State',
    [RegionalStrings.EMAIL_CUSTOMISATION]: 'E-mail customization',
    [RegionalStrings.INVOICE_TITLE]: 'Invoice',
  },
};

export const accountRegionToCountryName = (
  region: AccountRegions = AccountRegions.UK
): string => {
  const countryNames: Record<AccountRegions, string> = {
    [AccountRegions.AUSTRALIA]: 'Australia',
    [AccountRegions.CANADA]: 'Canada',
    [AccountRegions.IRELAND]: 'Ireland',
    [AccountRegions.NEW_ZEALAND]: 'New Zealand',
    [AccountRegions.SOUTH_AFRICA]: 'South Africa',
    [AccountRegions.UK]: 'United Kingdom',
    [AccountRegions.US]: 'United States',
  };
  return countryNames[region];
};

export const getRegionalTextString = (
  region = AccountRegions.UK,
  label?: RegionalStrings
) => {
  if (!label) return 'No label';
  const desiredLabel = regionalTextStringMappings?.[region]?.[label];

  // return UK label if for some reason the desired label can't be found
  return (
    desiredLabel ||
    regionalTextStringMappings[AccountRegions.UK]?.[label] ||
    'Missing label'
  );
};

/**
 * Region-aware placeholder for address search inputs, e.g.
 * "Start typing address or postcode" (UK) or "Start typing address or ZIP code" (US).
 */
export const getAddressSearchPlaceholder = (region = AccountRegions.UK) => {
  const postcodeLabel = getRegionalTextString(region, RegionalStrings.POSTCODE);
  // Lowercase the leading word for natural mid-sentence use, but keep acronyms like "ZIP" uppercase.
  const term = /^[A-Z]{2,}\b/.test(postcodeLabel)
    ? postcodeLabel
    : postcodeLabel.charAt(0).toLowerCase() + postcodeLabel.slice(1);
  return `Start typing address or ${term}`;
};

export const getISOCodeTextString = (
  isoCode?: CurrencyCodes,
  label?: RegionalStrings
) => {
  if (!isoCode) return '';
  if (!label) return 'No label';
  const desiredLabel =
    regionalTextStringMappings?.[getAccountRegionFromIsoCode(isoCode)]?.[label];

  // return UK label if for some reason the desired label can't be found
  return (
    desiredLabel ||
    regionalTextStringMappings[AccountRegions.UK]?.[label] ||
    'Missing label'
  );
};

export enum RegionalFormattingTypes {
  SORT_CODE = 'sortCode',
  ACCOUNT_NUMBER = 'accountNumber',
}

const regionalFormattingMap: any = {
  // TODO: ZA
  [RegionalFormattingTypes.SORT_CODE]: {
    [AccountRegions.AUSTRALIA]: (value: any) =>
      `${value.slice(0, 3)}-${value.slice(3)}`,
  },
  [RegionalFormattingTypes.ACCOUNT_NUMBER]: {
    [AccountRegions.AUSTRALIA]: (value: any) =>
      `${value.slice(0, 4)} ${value.slice(4)}`,
  },
};

export const getRegionalFormattedValue = (
  region = AccountRegions.UK,
  format: RegionalFormattingTypes,
  value: string | number
) => {
  // apply a formatting function or just return the raw value
  return regionalFormattingMap?.[format]?.[region]
    ? regionalFormattingMap?.[format]?.[region](value)
    : value;
};

export const getCurrencyIsoCode = (accountRegion = AccountRegions.UK) => {
  return currencyCodeForRegion[accountRegion] ?? CurrencyCodes.GBP;
};

export const getCurrencyIsoCodeLegacy = (
  accountRegion: AccountRegions | null = AccountRegions.UK
) => {
  return currencyCodeForRegion[
    accountRegion ?? AccountRegions.UK
  ].toLowerCase();
};

export const getAccountRegionFromIsoCode = (currencyCode: CurrencyCodes) => {
  switch (currencyCode) {
    case CurrencyCodes.GBP:
      return AccountRegions.UK;
    case CurrencyCodes.USD:
      return AccountRegions.US;
    case CurrencyCodes.AUD:
      return AccountRegions.AUSTRALIA;
    case CurrencyCodes.CAD:
      return AccountRegions.CANADA;
    case CurrencyCodes.EUR:
      return AccountRegions.IRELAND;
    case CurrencyCodes.NZD:
      return AccountRegions.NEW_ZEALAND;
    case CurrencyCodes.ZAR:
      return AccountRegions.SOUTH_AFRICA;
    default:
      checkUnreachable(currencyCode);

      // default to UK
      return AccountRegions.UK;
  }
};

export const getAccountRegionFromCurrencyCodeLegacy = (
  currencyCode: string
): AccountRegions | undefined => {
  return Object.entries(currencyCodeForRegion).find(
    ([, code]) => code.toLowerCase() === currencyCode.toLowerCase()
  )?.[0] as AccountRegions | undefined;
};

export const getAccountRegionFromPhoneNumber = (contactNumber = '') => {
  const cleanNumber = contactNumber.trim();
  switch (true) {
    case cleanNumber.startsWith('+353'):
      return AccountRegions.IRELAND;
    case cleanNumber.startsWith('+61'):
      return AccountRegions.AUSTRALIA;
    case cleanNumber.startsWith('+64'):
      return AccountRegions.NEW_ZEALAND;
    case cleanNumber.startsWith('+27'):
      return AccountRegions.SOUTH_AFRICA;
    case cleanNumber.startsWith('+1'):
      return AccountRegions.US;
    default:
      return AccountRegions.UK;
  }
};

export const getPayacaTaxCodeDescriptionsToApplyToSubscription = (
  accountRegion: AccountRegions
) => {
  switch (accountRegion) {
    case AccountRegions.AUSTRALIA:
    // fall-through
    case AccountRegions.CANADA:
    // fall-through
    case AccountRegions.NEW_ZEALAND:
    // fall-through
    case AccountRegions.SOUTH_AFRICA:
    // fall-through
    case AccountRegions.US:
      return ['UK VAT Zero Rated'];
    case AccountRegions.IRELAND:
      return [];
    default:
      return ['UK VAT'];
  }
};

export enum DateFormats {
  TODAY = 'today',
  DAY_MONTH = 'day_month',
  DAY_MONTH_SHORT = 'day_month_short',
  SHORT = 'short',
  MID = 'mid',
  TIMESTAMP = 'timestamp',
  TIMESTAMP_SHORT = 'timestamp_short',
  TIMESTAMP_DAY_MONTH = 'timestamp_day_month',
  SHORT_DELIMITED = 'short_delimited',
  SHORT_READABLE = 'short_readable',
}

const UK_IE_DATE_FORMATS = {
  [DateFormats.TODAY]: DATE_TODAY_FORMAT,
  [DateFormats.SHORT]: DATE_SHORT_FORMAT,
  [DateFormats.DAY_MONTH]: DATE_DAY_MONTH_FORMAT,
  [DateFormats.DAY_MONTH_SHORT]: DATE_SHORT_DAY_MONTH_FORMAT,
  [DateFormats.MID]: DATE_MID_FORMAT,
  [DateFormats.SHORT_DELIMITED]: DATE_SHORT_DELIMITED_FORMAT,
  [DateFormats.TIMESTAMP]: DATE_TIMESTAMP_FORMAT,
  [DateFormats.TIMESTAMP_SHORT]: DATE_TIMESTAMP_SHORT_FORMAT,
  [DateFormats.TIMESTAMP_DAY_MONTH]: DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  [DateFormats.SHORT_READABLE]: DATE_SHORT_READABLE_FORMAT,
};

export const dateFormatMap: {
  [key in AccountRegions]: { [key in DateFormats]: any };
} = {
  [AccountRegions.AUSTRALIA]: {
    [DateFormats.TODAY]: DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: DATE_SHORT_FORMAT,
    [DateFormats.DAY_MONTH]: DATE_DAY_MONTH_FORMAT,
    [DateFormats.DAY_MONTH_SHORT]: DATE_SHORT_DAY_MONTH_FORMAT,
    [DateFormats.MID]: DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.CANADA]: {
    [DateFormats.TODAY]: CA_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: CA_DATE_SHORT_FORMAT,
    [DateFormats.DAY_MONTH]: CA_DATE_DAY_MONTH_FORMAT,
    [DateFormats.DAY_MONTH_SHORT]: CA_DATE_SHORT_DAY_MONTH_FORMAT,
    [DateFormats.MID]: CA_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: CA_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: CA_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: CA_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: CA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: CA_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.IRELAND]: UK_IE_DATE_FORMATS,
  [AccountRegions.NEW_ZEALAND]: {
    [DateFormats.TODAY]: DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: DATE_SHORT_FORMAT,
    [DateFormats.DAY_MONTH]: DATE_DAY_MONTH_FORMAT,
    [DateFormats.DAY_MONTH_SHORT]: DATE_SHORT_DAY_MONTH_FORMAT,
    [DateFormats.MID]: DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.SOUTH_AFRICA]: {
    [DateFormats.TODAY]: ZA_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: ZA_DATE_SHORT_FORMAT,
    [DateFormats.DAY_MONTH]: ZA_DATE_DAY_MONTH_FORMAT,
    [DateFormats.DAY_MONTH_SHORT]: ZA_DATE_SHORT_DAY_MONTH_FORMAT,
    [DateFormats.MID]: ZA_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: ZA_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: ZA_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: ZA_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: ZA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: ZA_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.UK]: UK_IE_DATE_FORMATS,
  [AccountRegions.US]: {
    [DateFormats.TODAY]: US_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: US_DATE_SHORT_FORMAT,
    [DateFormats.DAY_MONTH]: US_DATE_DAY_MONTH_FORMAT,
    [DateFormats.DAY_MONTH_SHORT]: US_DATE_SHORT_DAY_MONTH_FORMAT,
    [DateFormats.MID]: US_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: US_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: US_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: US_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: US_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: US_DATE_SHORT_READABLE_FORMAT,
  },
};

export const getInternationalDateFormatByLocale = (
  format: DateFormats,
  locale?: string
) => {
  switch (locale) {
    case 'en-CA':
      return dateFormatMap[AccountRegions.CANADA][format];
    case 'en-IE':
      return dateFormatMap[AccountRegions.IRELAND][format];
    case 'en-NZ':
      return dateFormatMap[AccountRegions.NEW_ZEALAND][format];
    case 'en-ZA':
      return dateFormatMap[AccountRegions.SOUTH_AFRICA][format];
    case 'en-US':
      return dateFormatMap[AccountRegions.US][format];
    case 'en-GB':
      return dateFormatMap[AccountRegions.UK][format];
    default:
      return dateFormatMap[AccountRegions.UK][format];
  }
};

const UK_IE_MOMENT_DATE_FORMATS = {
  [DateFormats.TODAY]: MOMENT_DATE_TODAY_FORMAT,
  [DateFormats.SHORT]: MOMENT_DATE_SHORT_FORMAT,
  [DateFormats.MID]: MOMENT_DATE_MID_FORMAT,
  [DateFormats.SHORT_DELIMITED]: MOMENT_DATE_SHORT_DELIMITED_FORMAT,
  [DateFormats.TIMESTAMP]: MOMENT_DATE_TIMESTAMP_FORMAT,
  [DateFormats.TIMESTAMP_DAY_MONTH]: MOMENT_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
  [DateFormats.TIMESTAMP_SHORT]: MOMENT_DATE_TIMESTAMP_SHORT_FORMAT,
  [DateFormats.SHORT_READABLE]: MOMENT_DATE_SHORT_READABLE_FORMAT,
};

const dateMomentFormatMap: {
  [key in AccountRegions]: Partial<{ [key in DateFormats]: any }>;
} = {
  [AccountRegions.AUSTRALIA]: {
    [DateFormats.TODAY]: MOMENT_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: MOMENT_DATE_SHORT_FORMAT,
    [DateFormats.MID]: MOMENT_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: MOMENT_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: MOMENT_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: MOMENT_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: MOMENT_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: MOMENT_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.CANADA]: {
    [DateFormats.TODAY]: MOMENT_CA_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: MOMENT_CA_DATE_SHORT_FORMAT,
    [DateFormats.MID]: MOMENT_CA_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: MOMENT_CA_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: MOMENT_CA_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: MOMENT_CA_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]:
      MOMENT_CA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: MOMENT_CA_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.IRELAND]: UK_IE_MOMENT_DATE_FORMATS,
  [AccountRegions.NEW_ZEALAND]: {
    [DateFormats.TODAY]: MOMENT_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: MOMENT_DATE_SHORT_FORMAT,
    [DateFormats.MID]: MOMENT_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: MOMENT_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: MOMENT_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: MOMENT_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]: MOMENT_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.SHORT_READABLE]: MOMENT_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.SOUTH_AFRICA]: {
    [DateFormats.TODAY]: MOMENT_ZA_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: MOMENT_ZA_DATE_SHORT_FORMAT,
    [DateFormats.MID]: MOMENT_ZA_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: MOMENT_ZA_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: MOMENT_ZA_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]:
      MOMENT_ZA_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: MOMENT_ZA_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.SHORT_READABLE]: MOMENT_ZA_DATE_SHORT_READABLE_FORMAT,
  },
  [AccountRegions.UK]: UK_IE_MOMENT_DATE_FORMATS,
  [AccountRegions.US]: {
    [DateFormats.TODAY]: MOMENT_US_DATE_TODAY_FORMAT,
    [DateFormats.SHORT]: MOMENT_US_DATE_SHORT_FORMAT,
    [DateFormats.MID]: MOMENT_US_DATE_MID_FORMAT,
    [DateFormats.SHORT_DELIMITED]: MOMENT_US_DATE_SHORT_DELIMITED_FORMAT,
    [DateFormats.TIMESTAMP]: MOMENT_US_DATE_TIMESTAMP_FORMAT,
    [DateFormats.TIMESTAMP_DAY_MONTH]:
      MOMENT_US_DATE_TIMESTAMP_DAY_MONTH_FORMAT,
    [DateFormats.TIMESTAMP_SHORT]: MOMENT_US_DATE_TIMESTAMP_SHORT_FORMAT,
    [DateFormats.SHORT_READABLE]: MOMENT_US_DATE_SHORT_READABLE_FORMAT,
  },
};
export const getInternationalMomentDateFormatByRegion = (
  format: DateFormats,
  accountRegion: AccountRegions
) => {
  return (
    dateMomentFormatMap?.[accountRegion]?.[format] ||
    MOMENT_DATE_TIMESTAMP_SHORT_FORMAT
  );
};

// TODO: Expand to cover the many timezones in different countries
export const getInternationalTimezoneByRegion = (
  accountRegion: AccountRegions
) => {
  switch (accountRegion) {
    case AccountRegions.AUSTRALIA:
      // FIXME: Why do we use Australia/Brisbane here, but Australia/Sydney above?
      return 'Australia/Brisbane';
    case AccountRegions.CANADA:
      return 'Canada/Atlantic';
    case AccountRegions.IRELAND:
      return 'Europe/Dublin';
    case AccountRegions.NEW_ZEALAND:
      return 'Pacific/Auckland';
    case AccountRegions.SOUTH_AFRICA:
      return 'Africa/Johannesburg';
    case AccountRegions.US:
      return 'America/New_York';
    case AccountRegions.UK:
    // fall-through
    default:
      return 'Europe/London';
  }
};

/**
 * The IANA timezone an account's content is rendered in: its preferred
 * timezone when set (blank counts as unset), otherwise the region's default.
 */
export const timezoneForAccount = (account: {
  region: AccountRegions;
  preferredTimezone?: string | null;
}): string =>
  account.preferredTimezone || getInternationalTimezoneByRegion(account.region);

const twilioAlphanumericSupportHandleMap: Record<AccountRegions, boolean> = {
  [AccountRegions.AUSTRALIA]: true,
  [AccountRegions.CANADA]: false,
  [AccountRegions.IRELAND]: false,
  [AccountRegions.NEW_ZEALAND]: false,
  [AccountRegions.SOUTH_AFRICA]: false,
  [AccountRegions.UK]: true,
  [AccountRegions.US]: false,
};

export const cansupportTwilioSenderHandle = (region: AccountRegions) =>
  twilioAlphanumericSupportHandleMap[region] || false;
